import React, { ReactNode, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlTooltip from 'features/common/StlTooltip';
import { classNames } from 'features/common/utils';
import { secondary_light_text_color } from 'theme/cemTheme';

import {
  DAY_NAMES,
  DAY_PART_INTERVALS,
  DayPartLabel,
  formatDayHours,
  formatDayTypes,
  hourBelongsToDayPart,
} from '../alertHelpers';
import { MetricRow } from '../components/MetricRow';

import styles from './AlertScheduleField.module.css';

interface AlertScheduleFieldProps {
  mode: 'view' | 'edit';
  scheduleDayTypes: number[];
  scheduleDayHours: number[];
  onDayTypesChange?: (dayTypes: number[]) => void;
  onDayHoursChange?: (dayHours: number[]) => void;
}

interface ScheduleChipProps {
  label: string;
  isSelected: boolean;
  onClick?: () => void;
  chipClass: string;
  tooltipTitle?: React.ReactNode;
}

function ScheduleChip({
  label,
  isSelected,
  onClick,
  chipClass,
  tooltipTitle,
}: ScheduleChipProps) {
  const className = classNames(chipClass, isSelected && styles.chipSelected);

  return (
    <StlTooltip title={tooltipTitle} placement="top">
      <Box className={className} onClick={onClick}>
        {isSelected && (
          <Box className={styles.chipCheckbox}>
            <FontAwesomeIcon
              name="square-check"
              sx={{
                color: 'rgba(32, 32, 32, 0.80)',
                stroke: 'var(--gold-col-light)',
              }}
            />
          </Box>
        )}
        <Typography variant="caption">{label}</Typography>
      </Box>
    </StlTooltip>
  );
}

interface ChipListProps {
  items: readonly any[];
  selectedItems: any;
  onToggle: (label: any) => void;
  chipClass: string;
  spacing?: number;
  tooltipTitleForLabel?: (label: string) => ReactNode;
}

function ScheduleChipList({
  items,
  selectedItems,
  onToggle,
  chipClass,
  spacing = 0.5,
  tooltipTitleForLabel,
}: ChipListProps) {
  return (
    <Stack direction="row" spacing={spacing}>
      {items.map((item) => (
        <ScheduleChip
          key={item.label}
          tooltipTitle={tooltipTitleForLabel?.(item.label)}
          label={item.label}
          isSelected={selectedItems.has(item.label)}
          onClick={() => onToggle(item.label)}
          chipClass={chipClass}
        />
      ))}
    </Stack>
  );
}

interface ViewScheduleChipListProps {
  items: string[];
  chipClass: string;
  emptyMessage: string;
}

function ViewScheduleChipList({
  items,
  chipClass,
  emptyMessage,
}: ViewScheduleChipListProps) {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5}>
      {items.map((item, index) => (
        <Box
          key={`${item}-${index}`}
          className={classNames(chipClass, styles.cursorDefault)}
        >
          <Typography variant="caption">{item}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function AlertScheduleField({
  mode,
  scheduleDayTypes,
  scheduleDayHours,
  onDayTypesChange,
  onDayHoursChange,
}: AlertScheduleFieldProps) {
  const isEdit = mode === 'edit';

  const selectedDayTypesSet = useMemo(
    () => new Set(scheduleDayTypes.map((dayNum) => DAY_NAMES[dayNum - 1])),
    [scheduleDayTypes],
  );

  const dayPartLabels = Object.keys(DAY_PART_INTERVALS) as DayPartLabel[];

  const selectedDayPartsSet = useMemo(() => {
    const selected = new Set<DayPartLabel>();
    dayPartLabels.forEach((label) => {
      if (scheduleDayHours.some((h) => hourBelongsToDayPart(h, label))) {
        selected.add(label);
      }
    });
    return selected;
  }, [scheduleDayHours]);

  const formattedDayTypes = useMemo(
    () => formatDayTypes(scheduleDayTypes),
    [scheduleDayTypes],
  );

  const formattedDayHours = useMemo(
    () => formatDayHours(scheduleDayHours),
    [scheduleDayHours],
  );

  const handleDayTypeToggle = useCallback(
    (dayLabel: string) => {
      if (!isEdit || !onDayTypesChange) return;

      const dayIndex = DAY_NAMES.indexOf(
        dayLabel as (typeof DAY_NAMES)[number],
      );
      if (dayIndex === -1) return;

      const dayNum = dayIndex + 1;
      const newDayTypes = scheduleDayTypes.includes(dayNum)
        ? scheduleDayTypes.filter((d) => d !== dayNum)
        : [...scheduleDayTypes, dayNum].sort((a, b) => a - b);

      onDayTypesChange(newDayTypes);
    },
    [isEdit, onDayTypesChange, scheduleDayTypes],
  );

  const handleDayPartToggle = useCallback(
    (dayPartLabel: DayPartLabel) => {
      if (!isEdit || !onDayHoursChange) return;

      const { startHour, endHour } = DAY_PART_INTERVALS[dayPartLabel];
      const hoursForDayPart = [startHour * 100 + endHour];
      const hasHoursForDayPart = scheduleDayHours.some((h) =>
        hourBelongsToDayPart(h, dayPartLabel),
      );

      if (hasHoursForDayPart) {
        // Remove day part from the schedule
        const newHours = scheduleDayHours.filter(
          (h) => !hourBelongsToDayPart(h, dayPartLabel),
        );
        onDayHoursChange(newHours);
      } else {
        const newHours = [
          ...new Set([...scheduleDayHours, ...hoursForDayPart]),
        ].sort((a, b) => a - b);
        onDayHoursChange(newHours);
      }
    },
    [isEdit, onDayHoursChange, scheduleDayHours],
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h4" color={secondary_light_text_color}>
        Set Alert Schedule
      </Typography>

      <MetricRow label="Day Type">
        {isEdit ? (
          <ScheduleChipList
            items={DAY_NAMES.map((day) => ({ label: day as string }))}
            selectedItems={selectedDayTypesSet}
            onToggle={handleDayTypeToggle}
            chipClass={styles.chipDay}
            spacing={0.5}
          />
        ) : (
          <ViewScheduleChipList
            items={formattedDayTypes}
            chipClass={styles.chipDay}
            emptyMessage="No days selected"
          />
        )}
      </MetricRow>

      <MetricRow label="Day Parts">
        {isEdit ? (
          <ScheduleChipList
            items={dayPartLabels.map((label) => ({ label }))}
            selectedItems={selectedDayPartsSet}
            onToggle={handleDayPartToggle}
            chipClass={styles.chipDayPart}
            spacing={0.5}
            tooltipTitleForLabel={(l) =>
              `This range includes: ${DAY_PART_INTERVALS[l as DayPartLabel].displayTimeRange}`
            }
          />
        ) : (
          <ViewScheduleChipList
            items={formattedDayHours}
            chipClass={styles.chipDayPart}
            emptyMessage="No day parts selected"
          />
        )}
      </MetricRow>
    </Stack>
  );
}
